// Build [{ id, label }] options for ClientFilterSelect from the per-role client
// sources. ids are stringified so they match native <select> string values.
import { sortByLabel } from "utils/optionSort";

// Keep the first option per client id, then order the survivors by the label the user
// actually reads. A user can have more than one source row for the same client (e.g.
// duplicate _manager / _member rows), which would otherwise list the same client twice.
//
// Sorting here rather than in each builder covers the merged lists too: a union of two
// separately-sorted lists is not itself sorted, and every builder below ends here.
const toUniqueSortedOptions = (options) => {
  const seen = new Set();
  return sortByLabel(
    options.filter((o) => {
      if (seen.has(o.id)) return false;
      seen.add(o.id);
      return true;
    })
  );
};

// One label rule for every source, so a client reads the same whichever role's list it
// came from. Falls back to the code alone, then the id, when a record carries no name.
// @since NEXT_VERSION
const clientOptionLabel = (code, name, id) => {
  if (code && name) return `${code} - ${name}`;
  return code || String(id);
};

export const toManagerClientOptions = (managerClients) =>
  toUniqueSortedOptions(
    (Array.isArray(managerClients) ? managerClients : []).filter(Boolean).map((c) => ({
      id: String(c.clientId),
      label: clientOptionLabel(c.clientCode, c.clientName, c.clientId),
    }))
  );

export const toMemberClientOptions = (memberships) =>
  toUniqueSortedOptions(
    (Array.isArray(memberships) ? memberships : [])
      .filter((m) => m?.client?.id != null)
      .map((m) => ({
        id: String(m.client.id),
        label: clientOptionLabel(m.client.code, m.client.name, m.client.id),
      }))
  );

// Client records keyed on their own id. Handles both field namings in use — the session
// cache calls them code/name, the Consolidated Report clientCode/clientName.
export const toClientListOptions = (clients) =>
  toUniqueSortedOptions(
    (Array.isArray(clients) ? clients : []).filter(Boolean).map((c) => ({
      id: String(c.id),
      label: clientOptionLabel(c.code ?? c.clientCode, c.name ?? c.clientName, c.id),
    }))
  );

// Union several option lists into one, keeping the first occurrence of each client id
// (e.g. a manager's subtree clients plus their own assignments — each listed once).
export const mergeClientOptionsById = (...optionLists) =>
  toUniqueSortedOptions(optionLists.flat().filter(Boolean));
